import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2, Mail, Phone, MapPin, ExternalLink, MoreVertical, ChevronDown, CalendarPlus, FilePlus2, StickyNote,
  Copy, Archive, FileSignature, Handshake,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { createClientAppointment } from "@/lib/api";
import { QuickContractDialog } from "@/components/help/QuickActionDialogs";
import { ADAPTERS } from "./clientAdapters";
import { KIND_META, STATUS_OPTIONS, fmtDate, initials, statusLabel, statusTone, errMsg, copyText } from "./clientMeta";
import { FormDialog, ServicesDialog, NoteDialog, EmailDialog, AppointmentDialog } from "./ClientDialogs";
import {
  OverviewTab, ContactsTab, ServicesTab, AppointmentsTab, DocumentsTab, NotesTab, BillingTab, ContractsTab, ReferralsTab,
} from "./ClientDetailTabs";

const TABS = [
  ["overview", "Overview"], ["contacts", "Contacts"], ["services", "Services"], ["appointments", "Appointments"],
  ["documents", "Documents"], ["notes", "Notes & Activity"], ["billing", "Billing"],
];
const MORE = [["contracts", "Contracts", FileSignature], ["referrals", "Referrals", Handshake]];

const StatusSelect = ({ kind, status, onChange }) => (
  <Select value={status || undefined} onValueChange={onChange}>
    <SelectTrigger className={`h-7 w-auto gap-1.5 rounded-full border px-3 text-xs font-semibold ${statusTone(kind, status)}`} data-testid="client-detail-status">
      {statusLabel(kind, status)}
    </SelectTrigger>
    <SelectContent>{STATUS_OPTIONS[kind].map((o) => <SelectItem key={o.value} value={o.value} data-testid={`client-status-opt-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

const ContactStrip = ({ view }) => (
  <div className="grid gap-3 sm:grid-cols-3 rounded-xl px-5 py-4" style={{ background: "#F8FAFC" }} data-testid="client-contact-strip">
    {[[Mail, "Email", view.email, view.email && `mailto:${view.email}`], [Phone, "Phone", view.phone, view.phone && `tel:${view.phone}`],
      [MapPin, "Location", view.address, null]].map(([Icon, label, value, href]) => (
      <div key={label} className="flex items-start gap-3 min-w-0">
        <Icon className="h-[18px] w-[18px] mt-0.5 shrink-0" style={{ color: "#0B2545" }} />
        <div className="min-w-0">
          <div className="text-xs" style={{ color: "#64748B" }}>{label}</div>
          {href ? <a href={href} className="text-sm font-medium text-blue-700 hover:underline break-all" data-testid={`client-strip-${label.toLowerCase()}`}>{value}</a>
            : <div className="text-sm font-medium break-words" style={{ color: "#0B2545" }} data-testid={`client-strip-${label.toLowerCase()}`}>{value || "—"}</div>}
        </div>
      </div>
    ))}
  </div>
);

const ActionBtn = ({ icon: Icon, label, onClick, href, tone, disabled, testid }) => {
  const cls = `inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border text-sm font-semibold whitespace-nowrap transition-colors ${tone} ${disabled ? "opacity-40 pointer-events-none" : ""}`;
  return href && !disabled
    ? <a href={href} className={cls} data-testid={testid}><Icon className="h-4 w-4" />{label}</a>
    : <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testid}><Icon className="h-4 w-4" />{label}</button>;
};

export default function ClientDetail({ row, allRows, onChanged, onOpenFull, onSelectRow, onArchived }) {
  const adapter = ADAPTERS[row.kind];
  const [record, setRecord] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dialog, setDialog] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    try { setRecord(await adapter.load(row.id)); }
    catch (e) { toast.error(errMsg(e, "Couldn't load this client.")); }
  }, [adapter, row.id]);
  useEffect(() => { setRecord(null); setTab("overview"); load(); }, [load]);

  if (!record) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} /></div>;

  const kind = row.kind;
  const meta = KIND_META[kind];
  const view = adapter.view(record);
  const refresh = async () => { await load(); setReloadKey((n) => n + 1); onChanged?.(); };

  const changeStatus = async (s) => {
    try { await adapter.setStatus(row.id, s); toast.success(`Status set to ${statusLabel(kind, s)}.`); refresh(); }
    catch (e) { toast.error(errMsg(e, "Couldn't change status.")); }
  };
  const archive = async () => {
    if (!window.confirm(`Archive ${view.name}? You can restore it from its pipeline.`)) return;
    try { await adapter.archive(row.id); toast.success("Archived."); onArchived?.(row); }
    catch (e) { toast.error(errMsg(e, "Couldn't archive.")); }
  };
  const emailClick = () => {
    if (!view.email) return;
    if (adapter.sendEmail) setDialog("email");
    else window.location.href = `mailto:${view.email}`;
  };
  const saveAppointment = async (a) => {
    await createClientAppointment(kind, row.id, { date: a.date, time: a.time, service: a.service, location: a.location || null, notes: a.notes || null });
    if (a.confirm && adapter.sendConfirmation) {
      try { await adapter.sendConfirmation(row.id, a); toast.success("Confirmation emailed."); }
      catch (e) { toast.error(errMsg(e, "Appointment saved, but the confirmation email failed.")); }
    }
  };
  const contractLink = adapter.contractLink(record);
  const tabProps = { kind, view, record, adapter, reloadKey, onEdit: setDialog };

  const body = {
    overview: <OverviewTab {...tabProps} onAddNote={() => setDialog("note")} />,
    contacts: <ContactsTab {...tabProps} />,
    services: <ServicesTab {...tabProps} />,
    appointments: <AppointmentsTab {...tabProps} onCreate={() => setDialog("appointment")} />,
    documents: <DocumentsTab {...tabProps} />,
    notes: <NotesTab {...tabProps} onSaveNote={async (t) => { await adapter.addNote(row.id, t); refresh(); }} />,
    billing: <BillingTab {...tabProps} />,
    contracts: <ContractsTab {...tabProps} onCreate={() => setDialog("contract")} />,
    referrals: <ReferralsTab {...tabProps} allRows={allRows} onSelect={onSelectRow} />,
  }[tab];
  const moreActive = MORE.find(([id]) => id === tab);

  return (
    <div className="flex flex-col min-h-full" data-testid={`client-detail-${kind}-${row.id}`}>
      <div className="p-5 sm:p-6 space-y-5">
        <header className="flex flex-col sm:flex-row sm:items-start gap-4">
          <span className="grid place-items-center h-16 w-16 rounded-full text-xl font-bold shrink-0 text-white" style={{ background: "linear-gradient(135deg,#0B2545,#2563EB)" }}>
            {initials(view.name)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-display text-2xl font-bold leading-tight break-words" style={{ color: "#0B2545" }} data-testid="client-detail-name">{view.name}</h2>
              <span className={`text-[11px] font-bold uppercase tracking-wide border rounded-md px-2 py-0.5 ${meta.badge}`} data-testid="client-detail-type">{meta.label}</span>
              <StatusSelect kind={kind} status={view.status} onChange={changeStatus} />
            </div>
            <p className="text-sm mt-1.5" style={{ color: "#64748B" }}>
              {row.location || view.address || "No location"} · Client since {fmtDate(view.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {kind !== "referral_partner" ? (
              <Button variant="outline" onClick={onOpenFull} className="h-10 font-semibold text-blue-700 border-slate-200" data-testid="client-open-full-profile">
                <ExternalLink className="h-4 w-4 mr-2" />Open Full Profile
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 border-slate-200" data-testid="client-detail-menu"><MoreVertical className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white">
                <DropdownMenuItem onClick={() => setDialog("info")} data-testid="client-menu-edit">Edit client</DropdownMenuItem>
                <DropdownMenuItem onClick={() => copyText(view.ref || row.id, "ID copied.")} data-testid="client-menu-copy-id">
                  <Copy className="h-3.5 w-3.5 mr-2" />Copy client ID
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={archive} className="text-red-700" data-testid="client-menu-archive"><Archive className="h-3.5 w-3.5 mr-2" />Archive client</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <ContactStrip view={view} />

        <nav className="flex items-center gap-1 border-b overflow-x-auto" style={{ borderColor: "#E2E8F0" }} data-testid="client-detail-tabs">
          {TABS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`client-tab-${id}`}
                    className={`px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === id ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-600 hover:text-[#0B2545]"}`}>
              {label}
            </button>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="client-tab-more"
                      className={`px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px flex items-center gap-1 transition-colors ${moreActive ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-slate-600 hover:text-[#0B2545]"}`}>
                {moreActive ? moreActive[1] : "More"}<ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white">
              {MORE.map(([id, label, Icon]) => (
                <DropdownMenuItem key={id} onClick={() => setTab(id)} data-testid={`client-more-${id}`}><Icon className="h-3.5 w-3.5 mr-2" />{label}</DropdownMenuItem>
              ))}
              {kind !== "referral_partner" ? (
                <DropdownMenuItem onClick={onOpenFull} data-testid="client-more-full-profile"><ExternalLink className="h-3.5 w-3.5 mr-2" />Full profile</DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div key={tab} className="animate-in fade-in duration-200">{body}</div>
      </div>

      <div className="mt-auto lg:sticky bottom-0 bg-white border-t px-4 sm:px-6 pt-4 pb-20 lg:pb-4 grid grid-cols-2 sm:flex sm:flex-wrap gap-2.5 rounded-b-xl" style={{ borderColor: "#E2E8F0" }} data-testid="client-quick-actions">
        <ActionBtn icon={Phone} label="Call" href={view.phone ? `tel:${view.phone}` : null} disabled={!view.phone} tone="border-teal-200 bg-teal-50/60 text-teal-800 hover:bg-teal-50" testid="client-action-call" />
        <ActionBtn icon={Mail} label="Email" onClick={emailClick} disabled={!view.email} tone="border-blue-200 bg-blue-50/70 text-blue-800 hover:bg-blue-50" testid="client-action-email" />
        <ActionBtn icon={CalendarPlus} label="Create Appointment" onClick={() => setDialog("appointment")} disabled={!adapter.appointments} tone="border-[#0B2545]/20 text-[#0B2545] hover:bg-slate-50" testid="client-action-appointment" />
        <ActionBtn icon={FilePlus2} label="Create Contract" onClick={() => setDialog("contract")} disabled={!contractLink} tone="border-[#D4AF37]/50 text-[#7A5E12] hover:bg-[#D4AF37]/10" testid="client-action-contract" />
        <ActionBtn icon={StickyNote} label="Add Note" onClick={() => setDialog("note")} tone="border-slate-300 text-[#0B2545] hover:bg-slate-50" testid="client-action-note" />
      </div>

      <FormDialog open={dialog === "info"} onClose={() => setDialog(null)} title="Edit client information" testid="client-info-dialog"
                  fields={adapter.info.fields} initial={adapter.info.values(record)} onSave={(v) => adapter.info.save(row.id, v)} onSaved={refresh} />
      <FormDialog open={dialog === "contact"} onClose={() => setDialog(null)} title="Edit primary contact" testid="client-contact-dialog"
                  fields={adapter.contact.fields} initial={adapter.contact.values(record)} onSave={(v) => adapter.contact.save(row.id, v)} onSaved={refresh} />
      {adapter.billing ? (
        <FormDialog open={dialog === "billing"} onClose={() => setDialog(null)} title="Edit billing" testid="client-billing-dialog"
                    fields={adapter.billing.fields} initial={adapter.billing.values(record)} onSave={(v) => adapter.billing.save(row.id, v)} onSaved={refresh} />
      ) : null}
      <ServicesDialog open={dialog === "services"} onClose={() => setDialog(null)} current={view.services}
                      onSave={(list) => adapter.saveServices(row.id, list)} onSaved={refresh} />
      <NoteDialog open={dialog === "note"} onClose={() => setDialog(null)} onSave={(t) => adapter.addNote(row.id, t)} onSaved={refresh} />
      {adapter.sendEmail ? (
        <EmailDialog open={dialog === "email"} onClose={() => setDialog(null)} to={view.email} onSave={(m) => adapter.sendEmail(row.id, m)} onSaved={refresh} />
      ) : null}
      <AppointmentDialog open={dialog === "appointment"} onClose={() => setDialog(null)} services={view.services} defaultLocation={view.address}
                         canConfirm={!!adapter.sendConfirmation && !!view.email} onSave={saveAppointment} onSaved={refresh} />
      <QuickContractDialog open={dialog === "contract"} onClose={() => setDialog(null)} link={contractLink} onSaved={refresh} />
    </div>
  );
}
